import React from 'react';
import IconButton from '../IconButton/IconButton';
import styles from './EncourageShareModal.scss';
import Svg from '@justgiving/svg';
import FacebookShare from '../SocialButtons/FacebookShare';
import socialShareImage from './assets/social-share.svg';
import isDesktopViewport from '../../utils/isDesktopViewport';
import usePageQuery, {
  buildQuery,
  UsePageQueryResult,
} from '../../hooks/usePageQuery';
import gql from 'graphql-tag';
import classNames from 'classnames';
import { closeIcon } from '@justgiving/icons';
import { useTranslation } from 'react-i18next';
import { Button } from '@justgiving/react-tracking';
import { EncourageShareOwnerFragment } from '../../../types/__generated__';
import useAbEnrol from '../../hooks/useAbEnrol';
import useAbMarkSuccess from '../../hooks/useAbMarkSuccess';
import { usePageOwner } from '../../hooks/usePageOwner';

interface EncourageShareModalProps {
  onClose: () => void;
  isEnrolledIn30SecondsModal: Boolean;
}

const FRAGMENT = buildQuery(gql`
  fragment EncourageShareOwner on Page {
    owner {
      name
      avatar
    }
  }
`);

const EncourageShareModal = React.forwardRef(
  (
    { onClose, isEnrolledIn30SecondsModal }: EncourageShareModalProps,
    ref?: React.Ref<HTMLDivElement>,
  ) => {
    const { isPageOwner } = usePageOwner();
    const { enrolledInVariation } = useAbEnrol(
      'fr_growth_share_prompt_profile_image',
    );
    const markSuccessSharePromptProfileImage = useAbMarkSuccess(
      'fr_growth_share_prompt_profile_image',
      'facebook-share',
    );
    const onShareClick = () => {
      markSuccess();
      onClose();
    };
    const markSuccess30SecondsModal = useAbMarkSuccess(
      'fr_growth_encourage_share_time_30_seconds',
      'share-modal',
    );
    const markSuccess = () => {
      markSuccessSharePromptProfileImage.markSuccess();
      if (isEnrolledIn30SecondsModal) {
        markSuccess30SecondsModal.markSuccess();
      }
    };
    const isEnrolledInSharePromptProfileImage = enrolledInVariation === 'B';
    const result = usePageQuery<EncourageShareOwnerFragment>(FRAGMENT);
    const { t } = useTranslation('common', { useSuspense: false });

    if (isPageOwner || !UsePageQueryResult.hasData(result) || !result.data) {
      return null;
    }

    const { owner } = result.data;
    const avatarUrl = owner?.avatar;
    const smallPrintMessage = isDesktopViewport()
      ? t('page.encourageShareModalSmallPrintDesktop')
      : t('page.encourageShareModalSmallPrintMobile');
    const facebookButtonText = isDesktopViewport()
      ? ''
      : t('page.encourageShareModalFacebookButtonMobile');

    return (
      <div
        aria-label={t('page.encourageshareModal')}
        className={styles.wrapper}
        data-qa="share-modal"
        ref={ref}
      >
        <IconButton
          className={styles.close}
          onClick={onClose}
          title={t('page.closeEncourageShareModal')}
        >
          <Svg markup={closeIcon} />
        </IconButton>
        <div className={styles.image}>
          <img
            alt={t('page.encourageShareModalSocialShareImage')}
            className={classNames(
              styles.social,
              isEnrolledInSharePromptProfileImage && styles.avatar,
            )}
            src={
              (isEnrolledInSharePromptProfileImage && avatarUrl) ||
              socialShareImage
            }
          />
        </div>
        <div className={styles.description}>
          <h1 className={styles.heading}>
            {t('page.encourageShareModalTitle')}
          </h1>
          <div className={styles.message}>
            {t('page.encourageShareModalMessage', { pageOwner: owner?.name })}
          </div>
          <FacebookShare
            showPrefix={true}
            buttonText={facebookButtonText}
            onClick={onShareClick}
            trackingData={{ page_section: 'page > share prompt' }}
          />
          <p>
            <Button
              className={styles.later}
              onClick={onClose}
              trackingData={{
                action_name: 'click',
                subtype: 'button',
                page_section: 'share with friends prompt',
                event: 'click',
                event_value: 'close',
              }}
            >
              {t('page.encourageShareModalLaterDismiss')}
            </Button>
          </p>
          <p className={styles.print}>{smallPrintMessage}</p>
        </div>
      </div>
    );
  },
);

EncourageShareModal.displayName = 'EncourageShareModal';

export default EncourageShareModal;
